import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CssBaseline,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import ErrorComponent from '@/components/utils/ErrorComponent';
import APIErrorCodes from '@/utils/error_codes/APIErrorCodes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { useAllGroupUserStatuses } from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import { getAllOrgCompassDevices } from '@/repository/OrgCompassDevicesDAO';
import { getDFUInformation } from '@/repository/DFUDAO';
import DeviceManagementRouteBar from './DeviceManagementRouteBar';
import DeviceTable from './DeviceTable';
import UserDashboard from '@/views/UserViews/home/user_dashboard/UserDashboard';
import type { OrgCompassDeviceListItem } from '@/models/OrgCompassDevice/OrgCompassDeviceListItem';
import type { FirmwareLookupTableOnAzure } from '@/../generated-client/models';

const DeviceManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showErrorToast } = useToast();
  const { organization, groups, pvdConfigurations, loadGroups } =
    useOrganizationUpdaterProvider();
  const allGroupUserStatuses = useAllGroupUserStatuses();

  const [devices, setDevices] = useState<OrgCompassDeviceListItem[]>([]);
  const [firmwareLookup, setFirmwareLookup] =
    useState<FirmwareLookupTableOnAzure | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Empty groups[] while organization.groups still has keys means "still
  // hydrating," not "no users / device-only." Do not show a clickable table
  // yet in that case (see web plan Task 13 / Assumption 5).
  const groupsReady = useMemo(() => {
    if (!organization) return false;
    const expectedGroupCount = Object.keys(organization.groups ?? {}).length;
    return expectedGroupCount === 0 || groups.length > 0;
  }, [organization, groups]);

  const pageReady = !loading && groupsReady;

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const data = await getAllOrgCompassDevices();
      setDevices(data);
    } catch (error) {
      console.error('Error fetching org compass devices:', error);
      setFetchError(true);
      showErrorToast(t(StringKeys.SOMETHING_WENT_WRONG));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    let cancelled = false;

    const fetchLookup = async () => {
      const cachedData = sessionStorage.getItem('firmwareLookup');
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          if (!cancelled) {
            setFirmwareLookup(parsedData);
          }
          return;
        } catch {
          sessionStorage.removeItem('firmwareLookup');
        }
      }
      try {
        const data = await getDFUInformation();
        if (!cancelled) {
          setFirmwareLookup(data);
          sessionStorage.setItem('firmwareLookup', JSON.stringify(data));
        }
      } catch {
        // Table still renders; outdated icons stay hidden without lookup data.
      }
    };

    fetchLookup();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh also re-fetches groups: OrganizationProvider only re-fetches
  // groups when the `organization` object reference changes, which a device
  // list refresh does not trigger. Without this, a departed org member's
  // last_auth_id (still present on the device row — it's a historical fact,
  // not nulled server-side) would keep resolving against stale groups and
  // open the full UserDashboard instead of the device-only view.
  const handleRefresh = useCallback(() => {
    loadDevices();
    loadGroups();
  }, [loadDevices, loadGroups]);

  if (!organization) return null;

  const renderContent = () => {
    // Fetch errors surface immediately (do not wait on groups for an error banner).
    if (fetchError) return <ErrorComponent error={APIErrorCodes.FETCH_ERROR} />;
    // Wait for BOTH the device fetch and groups hydration before table / empty.
    if (!pageReady) return <LoadingSpinner />;
    if (devices.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 6,
            width: '100%',
          }}
        >
          <Typography variant="h6">{t(StringKeys.NO_DEVICES_YET)}</Typography>
        </Box>
      );
    }
    return (
      <DeviceTable
        devices={devices}
        groups={groups}
        firmwareLookup={firmwareLookup}
      />
    );
  };

  return (
    <>
      <CssBaseline />
      <DeviceManagementRouteBar
        onRefresh={handleRefresh}
        isLoading={loading || !groupsReady}
      />
      <Box
        sx={{
          width: '100%',
          ...(isMobile ? { px: 2, pt: 1, mt: 6 } : { pl: '260px', pr: '20px' }),
          position: 'relative',
          zIndex: 1,
        }}
      >
        {renderContent()}
        <UserDashboard
          organization={organization}
          groups={groups}
          allGroupUserStatuses={allGroupUserStatuses}
          pvdConfigurations={pvdConfigurations ?? undefined}
        />
      </Box>
    </>
  );
};

export default DeviceManagement;
